import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  Search,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  X,
  Code,
  CheckCircle,
} from "lucide-react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

function formatLogTime(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return ts;
  }
}

export default function ErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedError, setSelectedError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadErrors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (levelFilter !== "all") params.level = levelFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get("/super-admin/logs/errors", { params });
      setErrors(res.data || []);
    } catch (err) {
      toast.error("Failed to load error logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadErrors();
  }, [levelFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadErrors();
  };

  const handleClear = async () => {
    if (!window.confirm("Are you sure you want to clear all error logs?")) return;
    setClearing(true);
    try {
      await api.post("/super-admin/logs/errors/clear");
      toast.success("Error logs cleared");
      setErrors([]);
      setSelectedError(null);
    } catch (err) {
      toast.error("Failed to clear error logs");
    } finally {
      setClearing(false);
    }
  };

  const handleCopyTrace = () => {
    if (!selectedError?.traceback) return;
    navigator.clipboard.writeText(selectedError.traceback);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Traceback copied to clipboard");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <span>Real-time System Error Logs</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Intercepted unhandled exceptions, failed background jobs, and SMTP handshake errors across the platform.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
          <button
            onClick={loadErrors}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
            <span>Refresh</span>
          </button>

          {errors.length > 0 && (
            <button
              onClick={handleClear}
              disabled={clearing}
              className="px-3.5 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Clear History</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-2xl bg-slate-900/60 border border-slate-800">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search error messages, routes, or sources..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-950/80 border border-slate-700/60 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </form>

        <div className="flex items-center gap-1.5 self-start sm:self-center">
          {["all", "ERROR", "CRITICAL", "WARNING"].map((lvl) => (
            <button
              key={lvl}
              onClick={() => setLevelFilter(lvl)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                levelFilter === lvl
                  ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                  : "bg-slate-950/50 text-slate-400 hover:text-white border border-transparent"
              }`}
            >
              {lvl}
            </button>
          ))}
        </div>
      </div>

      {/* Error Logs List Table */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="py-2.5 px-4">Level</th>
                <th className="py-2.5 px-4">Timestamp</th>
                <th className="py-2.5 px-4">Source / Origin</th>
                <th className="py-2.5 px-4">Endpoint</th>
                <th className="py-2.5 px-4">Error Message</th>
                <th className="py-2.5 px-4 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {errors.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 text-xs">
                    {loading ? "Fetching logs..." : "No errors matching criteria. All systems smooth!"}
                  </td>
                </tr>
              ) : (
                errors.map((err) => {
                  const isCrit = err.level === "CRITICAL";
                  return (
                    <tr
                      key={err.id}
                      onClick={() => setSelectedError(err)}
                      className="hover:bg-slate-800/50 cursor-pointer transition-colors group"
                    >
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase ${
                            isCrit
                              ? "bg-rose-600 text-white"
                              : err.level === "WARNING"
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                              : "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                          }`}
                        >
                          {err.level}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-slate-400 font-mono text-[11px] whitespace-nowrap">
                        {formatLogTime(err.timestamp || err.created_at)}
                      </td>

                      <td className="py-3 px-4 text-slate-200 font-mono text-[11px]">
                        {err.source || "backend"}
                      </td>

                      <td className="py-3 px-4 text-slate-400 font-mono text-[11px] truncate max-w-xs">
                        {err.endpoint || "—"}
                      </td>

                      <td className="py-3 px-4 text-white font-medium max-w-md truncate">
                        {err.message}
                      </td>

                      <td className="py-3 px-4 text-right">
                        <span className="text-teal-400 text-xs font-semibold group-hover:underline">
                          View Stack →
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stack Trace Modal */}
      {selectedError && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
              <div className="flex items-center gap-2.5">
                <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-400 border border-rose-500/30 text-[10px] font-mono font-bold">
                  {selectedError.level}
                </span>
                <span className="text-sm font-bold text-white">Exception Stack Trace</span>
              </div>
              <button
                onClick={() => setSelectedError(null)}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-4 text-xs">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Message
                </div>
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono leading-relaxed">
                  {selectedError.message}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                  <div className="text-[10px] text-slate-500 uppercase">Endpoint</div>
                  <div className="font-mono text-slate-200 truncate">{selectedError.endpoint || "—"}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                  <div className="text-[10px] text-slate-500 uppercase">Client IP</div>
                  <div className="font-mono text-slate-200">{selectedError.client_ip || "—"}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80">
                  <div className="text-[10px] text-slate-500 uppercase">Timestamp</div>
                  <div className="font-mono text-slate-200">{formatLogTime(selectedError.timestamp || selectedError.created_at)}</div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Code className="w-3.5 h-3.5 text-teal-400" />
                    <span>Python Traceback</span>
                  </div>
                  {selectedError.traceback && (
                    <button
                      onClick={handleCopyTrace}
                      className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 font-semibold"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copied ? "Copied" : "Copy Trace"}</span>
                    </button>
                  )}
                </div>

                <pre className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 font-mono text-[11px] leading-relaxed overflow-x-auto whitespace-pre-wrap max-h-64">
                  {selectedError.traceback || "No detailed traceback recorded for this event."}
                </pre>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-slate-800 bg-slate-950/60 flex justify-end">
              <button
                onClick={() => setSelectedError(null)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
